import React, { useState } from 'react';
import {
  Flame,
  Loader,
  LogOut,
  Menu,
  X,
  User,
  Shield,
} from 'lucide-react';
import arcLogo from '../images/arc-logo.png';

/*******************************************************************************
 * Helper Components                                                            *
 ******************************************************************************/

const LiveStatus = ({ lastUpdated = new Date() }) => (
  <div className="hidden lg:flex items-center gap-3 text-xs">
    <div className="flex items-center gap-2">
      <span className="pulse-dot" aria-hidden="true" />
      <span className="font-mono uppercase tracking-[0.18em] text-arc-light">
        Live
      </span>
    </div>
    <span className="text-arc-dim">|</span>
    <span className="font-mono text-arc-muted">
      Updated {lastUpdated.toLocaleDateString()}
    </span>
  </div>
);

const BrandLogo = () => (
  <div className="flex items-center gap-3 sm:gap-4">
    {/* Company Logo */}
    <img
      src={arcLogo}
      alt="ARCrypto"
      className="h-8 sm:h-10 w-auto object-contain opacity-95"
    />

    {/* Divider */}
    <div className="h-6 sm:h-8 w-px bg-arc-line hidden xs:block" />

    {/* FIRE Tracker mark */}
    <div className="flex items-center gap-2 sm:gap-3">
      <div className="relative">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl border border-arc-deep bg-gradient-to-br from-arc-light to-arc-deep flex items-center justify-center shadow-glow">
          <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-arc-bg" strokeWidth={2.5} />
        </div>
      </div>

      <div className="flex flex-col leading-none">
        <h1 className="font-display font-semibold tracking-tightest text-base sm:text-xl text-arc-ink">
          <span className="hidden xs:inline">ARC</span>
          <span className="text-arc-gold">FIRE</span>
          <span>Tracker</span>
        </h1>
        <p className="eyebrow mt-1.5 hidden sm:block">
          What gets measured gets manifested
        </p>
      </div>
    </div>
  </div>
);

const VerifiedPill = ({ user }) => {
  const verified = user?.emailVerified;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em] ${
        verified ? 'text-arc-light' : 'text-arc-deep'
      }`}
    >
      <Shield size={10} />
      {verified ? 'Verified' : 'Unverified'}
    </span>
  );
};

const MobileMenu = ({ isOpen, onClose, user, onLogout, loggingOut, lastUpdated }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-arc-bg/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Menu */}
      <div className="fixed top-0 right-0 h-full w-80 max-w-full glass">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-arc-line">
            <span className="eyebrow">Account</span>
            <button
              onClick={onClose}
              className="btn-tactile p-2 rounded-lg text-arc-muted hover:text-arc-ink hover:bg-arc-elevated/5"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 p-5 space-y-6">
            {/* User Info */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full border border-arc-deep bg-gradient-to-br from-arc-light to-arc-deep flex items-center justify-center">
                <User className="w-5 h-5 text-arc-bg" strokeWidth={2.5} />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-arc-ink truncate">
                  {user?.displayName || user?.email || 'User'}
                </p>
                <div className="mt-1">
                  <VerifiedPill user={user} />
                </div>
              </div>
            </div>

            {/* Status Info */}
            <div className="space-y-3 pt-4 border-t border-arc-line">
              <div className="flex items-center gap-2">
                <span className="pulse-dot" aria-hidden="true" />
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-arc-light">
                  Live data active
                </span>
              </div>
              <div className="font-mono text-xs text-arc-muted">
                Last updated {lastUpdated.toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-5 border-t border-arc-line">
            <button
              onClick={onLogout}
              disabled={loggingOut}
              className="btn-tactile btn-outline w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-xl disabled:opacity-50"
            >
              {loggingOut ? (
                <Loader className="animate-spin" size={16} />
              ) : (
                <LogOut size={16} />
              )}
              {loggingOut ? 'Signing out…' : 'Sign Out'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/*******************************************************************************
 * Main Header Component                                                        *
 ******************************************************************************/

const Header = ({ user, onLogout, lastUpdated }) => {
  const [loggingOut, setLoggingOut] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    setMobileMenuOpen(false);
    await onLogout?.();
    setLoggingOut(false);
  };

  return (
    <>
      <header className="glass sticky top-0 z-40 border-b border-arc-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-18 py-3">
            {/* Brand */}
            <div className="flex-1 min-w-0">
              <BrandLogo />
            </div>

            {/* Live status (hidden on mobile/tablet) */}
            <div className="flex-shrink-0 mx-4">
              <LiveStatus lastUpdated={lastUpdated} />
            </div>

            {/* Desktop user + logout */}
            <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
              <div className="text-right">
                <p className="text-sm font-medium text-arc-ink">
                  {user?.displayName || user?.email || 'User'}
                </p>
                <div className="mt-0.5">
                  <VerifiedPill user={user} />
                </div>
              </div>

              <button
                onClick={handleLogout}
                disabled={loggingOut}
                className="btn-tactile btn-outline flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl disabled:opacity-50"
              >
                {loggingOut ? (
                  <Loader className="animate-spin" size={16} />
                ) : (
                  <LogOut size={16} />
                )}
                <span className="hidden xl:inline">
                  {loggingOut ? 'Signing out…' : 'Sign Out'}
                </span>
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden items-center gap-2">
              <div className="w-8 h-8 rounded-full border border-arc-deep bg-gradient-to-br from-arc-light to-arc-deep flex items-center justify-center">
                <User className="w-4 h-4 text-arc-bg" strokeWidth={2.5} />
              </div>
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="btn-tactile p-2 rounded-lg text-arc-muted hover:text-arc-ink hover:bg-arc-elevated/5"
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        onLogout={handleLogout}
        loggingOut={loggingOut}
        lastUpdated={lastUpdated}
      />
    </>
  );
};

/*******************************************************************************
 * Optional standalone logo variants                                            *
 ******************************************************************************/

export const LogoVariants = {
  CompactLogo: () => (
    <div className="flex items-center gap-3">
      <img src={arcLogo} alt="ARCrypto" className="h-6 sm:h-8 w-auto object-contain" />
      <div className="h-4 sm:h-6 w-px bg-arc-line" />
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-lg border border-arc-deep bg-gradient-to-br from-arc-light to-arc-deep flex items-center justify-center">
          <Flame className="w-3 h-3 text-arc-bg" strokeWidth={2.5} />
        </div>
        <span className="font-display font-semibold text-arc-ink">
          <span className="text-arc-gold">FIRE</span>Tracker
        </span>
      </div>
    </div>
  ),

  PrintLogo: () => (
    <div className="flex items-center justify-center gap-4">
      <img src={arcLogo} alt="ARCrypto" className="h-12 w-auto object-contain print:h-10" />
      <div className="h-10 w-px bg-arc-line" />
      <BrandLogo />
    </div>
  ),
};

export default Header;
